import { ForwardedRef, forwardRef, memo, PropsWithChildren, ReactNode } from 'react'

import { Box, Divider, Paper, PaperProps, styled, Typography, useTheme } from '@mui/material'
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react'

const RESIZABLE_DEFAULT_WIDTH = 480

const widthTransformer = (val: number) => {
  let width = RESIZABLE_DEFAULT_WIDTH
  if (val < 0) {
    width += Math.abs(val)
  }
  return width
}

const WrapperBase = styled(Paper)(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
}))

const Wrapper = motion.create(
  forwardRef(function RefForwardedWrapper(props: PaperProps, ref: ForwardedRef<HTMLDivElement>) {
    return <WrapperBase ref={ref} {...props} />
  }),
)

const TopSlot = styled(Box)(({ theme }) => ({
  minHeight: 44,
  margin: theme.spacing(1, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const Breadcrumbs = styled(Typography)({
  flex: 2,
})

const Actions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: 1,
})

const TitleSlot = styled(Box)(({ theme }) => ({
  margin: theme.spacing(3, 3, 4),
}))

const ContentSlot = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 3, 1),
  flex: 1,
}))

const DragHandle = styled(motion.div)(({ theme }) => ({
  width: theme.spacing(1),
  height: '6vh',
  position: 'absolute',
  right: RESIZABLE_DEFAULT_WIDTH + 10 + 'px',
  top: 'calc(100% - 47vh)',
  backgroundColor: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius * 4,
  cursor: 'col-resize',
  transformOrigin: 'right center',
}))

export type FoldoutPanelProps = {
  /**
   * Title component for the FoldoutPanel.
   * @remarks Only use `ManagedTitle` component as value.
   * @example
   * ```tsx
   * <FoldoutPanel
   *   title={<ManagedTitle {...managedTitleProps}>My FoldoutPanel</ManagedTitle>}
   *   {...panelProps}
   * />
   * ```
   */
  title: ReactNode
  breadcrumbs: string[]
  actions?: ReactNode
}

const FoldoutPanelRaw = ({ title, breadcrumbs, actions, children }: PropsWithChildren<FoldoutPanelProps>) => {
  const theme = useTheme()

  const x = useMotionValue(0)
  const transformedWidth = useTransform(x, widthTransformer)
  const springAnimatedWidth = useSpring(transformedWidth, { stiffness: 600, damping: 30, mass: 1 })

  return (
    <Wrapper style={{ width: springAnimatedWidth }}>
      <TopSlot>
        <Breadcrumbs variant="text" noWrap>
          {breadcrumbs.join(' / ')}
        </Breadcrumbs>

        {actions && <Actions>{actions}</Actions>}
      </TopSlot>

      <Divider />

      <TitleSlot>{title}</TitleSlot>

      <ContentSlot>{children}</ContentSlot>

      <DragHandle
        style={{ x }}
        title="Drag to resize the panel"
        dragMomentum={false}
        drag="x"
        dragConstraints={{
          left: -(RESIZABLE_DEFAULT_WIDTH / 2),
          right: 0,
        }}
        dragDirectionLock
        dragElastic={0.03}
        whileTap={{ scale: 1.4 }}
        whileDrag={{ backgroundColor: theme.palette.primary.dark, scale: 1.4 }}
      />
    </Wrapper>
  )
}

export const FoldoutPanel = memo(FoldoutPanelRaw)
